import React from 'react';
import { Box, Checkbox, FormControlLabel, FormGroup, Popover, Typography, Button } from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import { useEffect, useState } from 'react';
import { prepareColumnsAndGroups, CustomPagination } from '../../../shared/Helper';
import { startCase } from 'lodash';
import { Dialog, DialogContent, DialogTitle, IconButton } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import DateRangePickerInline from './DateRangePicker';
import theme from '../../../theme';

const TableComponent = (props) => {
  const {
    count,
    setPaginationModel,
    paginationModel,
    setFilters,
    list,
    filters,
    setCurrentFilterValue,
    currentFilterValue,
    setColValue,
    colValue,
    active,
    setSelectedRows,
    rowSelectionModel,
    setRowSelectionModel,
    enableSelection,
    filterableColumns
  } = props;

  const { data = [], initialColumns, loader } = props;
  const [row, setRows] = useState([]);
  const [anchorEl, setAnchorEl] = useState(null);
  const [currentFilter, setCurrentFilter] = useState("");
  const [visible, setOpen] = React.useState(false);
  const [selectedImage, setSelectedImage] = React.useState(null);
  const [dateRange, setDateRange] = useState({});
  
  const [tempFilterValue, setTempFilterValue] = useState({});

  const handleClose = () => {
    setOpen(false);
    setSelectedImage(null);
  };

  const handleCloseFilter = () => {
    setAnchorEl(null);
    setTempFilterValue({});
  };

  const handleOpenFilter = (event, filterType) => {
    setAnchorEl(event.currentTarget);
    setCurrentFilter(filterType);

    setTempFilterValue({
      [filterType]: currentFilterValue?.[filterType] || []
    });

    if (filterType === "time") {
      const timeFilter = filters?.find((item) => Object.keys(item).includes("time"));
      if (timeFilter && timeFilter.time && timeFilter.time.length === 2) {
        setDateRange(timeFilter.time); 
      } else {
        setDateRange([]);
      }
    }
  };

  useEffect(() => {
    if (data?.length) {
      setRows(data);
    } else {
      setRows([])
    }
  }, [data, active]);

  const handleSelectAll = (filterType, isChecked) => {
    setTempFilterValue((prevTemp) => ({
      ...prevTemp,
      [filterType]: isChecked ? [...list[filterType]] : [],
    }));
  };

  const handleFilterChange = (filterType, value) => {
    setTempFilterValue((prevTemp) => {
      const sourceArray = prevTemp?.[filterType] || [];
      const isSelected = sourceArray.includes(value);
      const newValues = isSelected
        ? sourceArray.filter((item) => item !== value)
        : [...sourceArray, value];

      return {
        ...prevTemp,
        [filterType]: newValues
      };
    });
  };

  const handleApplyFilter = () => {
    const filterType = currentFilter;
    const newValues = tempFilterValue[filterType] || [];

    setFilters((prevFilters) => {
      const updatedFilters = [...prevFilters];
      const index = updatedFilters.findIndex((f) => f.hasOwnProperty(filterType));


      if (index !== -1) {
        updatedFilters[index] = { [filterType]: newValues };
      } else {
        updatedFilters.push({ [filterType]: newValues });
      }

      return updatedFilters;
    });

    setCurrentFilterValue((prevFilters) => ({
      ...prevFilters,
      [filterType]: newValues,
    }));

    setAnchorEl(null);
    setTempFilterValue({});
  };

  const handleCancelFilter = () => {
    setTempFilterValue({});
    setAnchorEl(null);
  };

  const open = Boolean(anchorEl);

  const handleClearFilter = (filterType) => {
    if (filters?.length === 1 && filters?.find(obj => obj.hasOwnProperty(filterType))) {
      setFilters([])
    } else {
      setFilters((prevFilters) => {
        const targetFilter = prevFilters.find(obj => obj.hasOwnProperty(filterType));

        if (!targetFilter || !targetFilter[filterType]?.length) {
          return [];
        }

        const updatedFilters = prevFilters
          .filter(obj => !obj.hasOwnProperty(filterType));

        return [...updatedFilters, { [filterType]: [] }];
      });
    }

    setCurrentFilterValue((prevFilters) => ({
      ...prevFilters,
      [filterType]: [],
    }));

    setAnchorEl(null);
  };

  useEffect(() => {
    if (initialColumns?.length) {
      setColValue(initialColumns);
    }
  }, [initialColumns, setColValue]);

  const handleImageClick = (url) => {
    setSelectedImage(url);
    setOpen(true);
  }

  const { visibleColumns, columnGroupingModel } = prepareColumnsAndGroups(colValue, handleOpenFilter, [], handleImageClick, '', "", filterableColumns);

  const getValue = (values) => {
    if (currentFilter === 'status') {
      if (values === 0) return "Open";
      if (values === 1) return "Ready for Inspection";
      if (values === 2) return "Resolved";
      return "Deleted";
    } else if (currentFilter === "scope") {
      return startCase(values);
    } else {
      return values;
    }
  }

  const handleApply = (start, end) => {
    const startOfDay = new Date(start.getFullYear(), start.getMonth(), start.getDate(), 0, 0, 0);
    const endOfDay = new Date(end.getFullYear(), end.getMonth(), end.getDate(), 23, 59, 59);

    const unixTimestamps = [startOfDay, endOfDay].map(date =>
      Math.floor(date.getTime() / 1000)
    );

    setFilters((prevFilters) => {
      let sourceArray;

      if (prevFilters.length === 0) {
        sourceArray = [];
      } else {
        sourceArray = currentFilterValue?.[currentFilter] || [];
      }

      const isSelected = unixTimestamps.every((timestamp) => sourceArray.includes(timestamp));

      const newValues = isSelected
        ? sourceArray.filter((item) => !unixTimestamps.includes(item))
        : [...sourceArray, ...unixTimestamps];

      const updatedFilters = [...prevFilters];
      const index = updatedFilters.findIndex((f) => f.hasOwnProperty(currentFilter));

      if (index !== -1) {
        updatedFilters[index] = { [currentFilter]: newValues };
      } else {
        updatedFilters.push({ [currentFilter]: newValues });
      }

      return updatedFilters;
    });
    handleCloseFilter()
  };

  return (
    <Box>
      <div style={{ height: "71vh", width: '100%', backgroundColor: '#ffffff' }}>
        <DataGrid
          rows={row?.map((row, index) => ({...row }))}
          columns={visibleColumns}
          checkboxSelection={!enableSelection}
          rowSelectionModel={rowSelectionModel}
          onRowSelectionModelChange={(newSelection) => {
            setRowSelectionModel(newSelection);
            const selected = row
              .map((row, index) => ({ id: index, ...row }))
              .filter((r) => newSelection.includes(r.id));
            setSelectedRows(selected);
          }}
          experimentalFeatures={{ columnGrouping: true }}
          columnGroupingModel={columnGroupingModel}
          paginationModel={paginationModel}
          onPaginationModelChange={setPaginationModel}
          getRowHeight={() => 80}
          rowCount={count || 0}
          paginationMode="server"
          disableColumnMenu
          disableSelectionOnClick
          disableRowSelectionOnClick
          pagination={true}
          loading={loader}
          slots={{ pagination: CustomPagination }}
          sx={{
            '& .custom-header .MuiDataGrid-columnHeaderTitle': {
              flex: 1,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              width: '100%',
            },
            '& .MuiDataGrid-columnHeaderTitle': {
              fontWeight: 600,
              width: '100%',
            },
            '& .header-group': {
              display: 'flex',
              justifyContent: 'center',
              textAlign: 'center',
              width: '100%',
            },
            '& .css-14ylvtn-MuiTablePagination-root': {
              overflow: "hidden"
            },
            '& .custom-header': {
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              pl: 2,
              pr: 2,
            },
            '& .MuiDataGrid-columnHeader[data-field="block"]': {
              backgroundColor: filters?.find((item) => Object.keys(item).includes("block"))?.block?.length > 0 ? '#f16352' : '#f5f5f5',
              color: filters?.find((item) => Object.keys(item).includes("block"))?.block?.length > 0 ? 'white' : '',
            },
            '& .MuiDataGrid-columnHeader[data-field="row"]': {
              backgroundColor: filters?.find((item) => Object.keys(item).includes("row"))?.row?.length > 0 ? '#f16352' : '#f5f5f5',
              color: filters?.find((item) => Object.keys(item).includes("row"))?.row?.length > 0 ? 'white' : '',
            },
            '& .MuiDataGrid-columnHeader[data-field="scope"]': {
              backgroundColor: filters?.find((item) => Object.keys(item).includes("scope"))?.scope?.length > 0 ? '#f16352' : '#f5f5f5',
              color: filters?.find((item) => Object.keys(item).includes("scope"))?.scope?.length > 0 ? 'white' : '',
            },
            '& .MuiDataGrid-columnHeader[data-field="section"]': {
              backgroundColor: filters?.find((item) => Object.keys(item).includes("section"))?.section?.length > 0 ? '#f16352' : '#f5f5f5',
              color: filters?.find((item) => Object.keys(item).includes("section"))?.section?.length > 0 ? 'white' : '',
            },
            '& .MuiDataGrid-columnHeader[data-field="time_stamp"]': {
              backgroundColor: filters?.find((item) => Object.keys(item).includes("time"))?.time?.length > 0 ? '#f16352' : '#f5f5f5',
              color: filters?.find((item) => Object.keys(item).includes("time"))?.time?.length > 0 ? 'white' : '',
            },
            '& .MuiDataGrid-columnHeader[data-field="checkpoint"]': {
              backgroundColor: filters?.find((item) => Object.keys(item).includes("checkpoint_name"))?.checkpoint_name?.length > 0 ? '#f16352' : '#f5f5f5',
              color: filters?.find((item) => Object.keys(item).includes("checkpoint_name"))?.checkpoint_name?.length > 0 > 0 ? 'white' : '',
            },
            '& .MuiDataGrid-columnHeader[data-field="status"]': {
              backgroundColor: filters?.find((item) => Object.keys(item).includes("status"))?.status?.length > 0 ? '#f16352' : '#f5f5f5',
              color: filters?.find((item) => Object.keys(item).includes("status"))?.status?.length > 0 ? 'white' : '',
            },
            '& .MuiDataGrid-columnHeader[data-field="laydown"]': {
              backgroundColor: filters?.find((item) => Object.keys(item).includes("laydown"))?.laydown?.length > 0 ? '#f16352' : '#f5f5f5',
              color: filters?.find((item) => Object.keys(item).includes("laydown"))?.laydown?.length > 0 ? 'white' : '',
            },
            '& .MuiDataGrid-columnHeader[data-field="part_name"]': {
              backgroundColor: filters?.find((item) => Object.keys(item).includes("part_number"))?.part_number?.length > 0 ? '#f16352' : '#f5f5f5',
              color: filters?.find((item) => Object.keys(item).includes("part_number"))?.part_number?.length > 0 ? 'white' : '',
            },
            '& .MuiDataGrid-columnHeader[data-field="part_description"]': {
              backgroundColor: filters?.find((item) => Object.keys(item).includes("part_description"))?.part_description?.length > 0 ? '#f16352' : '#f5f5f5',
              color: filters?.find((item) => Object.keys(item).includes("part_description"))?.part_description?.length > 0 ? 'white' : '',
            },
          }}
        />
        <Popover
          open={open}
          anchorEl={anchorEl}
          onClose={handleCloseFilter}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
          PaperProps={{
            sx: {
              width: (currentFilter === "checkpoint_name" || currentFilter === "time" || currentFilter === "part_description") ? 320 : 230,
              height: currentFilter === "time" ? 380 : 350, // Increased height to accommodate buttons
              overflowY: currentFilter === "time" ? "hidden" : 'auto',
              overflowX: 'hidden',
              display: 'flex',
              flexDirection: 'column',
            },
          }}
        >
          {currentFilter !== "time" ?
            <>
              <div style={{ padding: "16px", flex: 1, overflowY: 'auto' }}>
                <div style={{ display: "flex", justifyContent: "space-between" }}>
                  <div className="font-medium" style={{ marginTop: 0, fontWeight: 600 }}>Filter</div>
                  <div
                    onClick={() => handleClearFilter(currentFilter)}
                    style={{ cursor: "pointer", color: "blue", fontWeight: 500, fontSize: 16 }}
                  >
                    Clear
                  </div>
                </div>
                <FormGroup>
                  <FormControlLabel
                    control={
                      <Checkbox
                        size="small"
                        checked={
                          (list?.[currentFilter]?.length > 0 &&
                            list?.[currentFilter]?.every((value) =>
                              tempFilterValue?.[currentFilter]?.includes(value)
                            )) || false
                        }
                        indeterminate={
                          (list?.[currentFilter]?.some((value) =>
                            tempFilterValue?.[currentFilter]?.includes(value)
                          ) &&
                            !list?.[currentFilter]?.every((value) =>
                              tempFilterValue?.[currentFilter]?.includes(value)
                            )) || false
                        }
                        onChange={(e) => handleSelectAll(currentFilter, e.target.checked)}
                      />
                    }
                    label={
                      <Box fontSize={16}>
                        {(list?.[currentFilter]?.length > 0 &&
                          list?.[currentFilter]?.every((value) =>
                            tempFilterValue?.[currentFilter]?.includes(value)
                          )) ? "Unselect All" : "Select All"}
                      </Box>
                    }
                  />
                  {(list?.[currentFilter]?.length ? list?.[currentFilter] : []).map((value) => (
                    <FormControlLabel
                      key={value}
                      control={
                        <Checkbox
                          size='small'
                          checked={tempFilterValue?.[currentFilter]?.includes(value) || false}
                          onChange={() => handleFilterChange(currentFilter, value)}
                        />
                      }
                      label={<Box fontSize={16}>{getValue(value)}</Box>}
                    />
                  ))}
                </FormGroup>
              </div>
              
              {/* Sticky buttons section */}
              <Box
                sx={{
                  borderTop: '1px solid #e0e0e0',
                  padding: '12px 16px',
                  display: 'flex',
                  justifyContent: 'space-between',
                  gap: 1,
                  backgroundColor: '#ffffff',
                }}
              >
                <Button
                  variant="outlined"
                  size="small"
                  onClick={handleCancelFilter}
                  sx={{ 
                    flex: 1,
                    textTransform: 'none',
                    borderColor: '#d0d0d0',
                    color: '#666',
                    '&:hover': {
                      borderColor: '#b0b0b0',
                      backgroundColor: '#f5f5f5'
                    }
                  }}
                >
                  Cancel
                </Button>
                <Button
                  variant="contained"
                  size="small"
                  onClick={handleApplyFilter}
                  sx={{ 
                    flex: 1,
                    textTransform: 'none',
                    backgroundColor: theme.palette.primary.main,
                    '&:hover': {
                      backgroundColor: theme.palette.primary.main
                    }
                  }}
                >
                  Apply
                </Button>
              </Box>
            </> :
            <DateRangePickerInline
              dateRange={dateRange}
              setDateRange={setDateRange}
              placeholder="Select date range"
              maxDate={new Date()}
              handleApply={handleApply}
              onClose={handleCloseFilter}
              handleClearFilter={() => handleClearFilter("time")}
            />}
        </Popover>
      </div>

      <Dialog open={visible} onClose={handleClose} maxWidth="md">
        <DialogTitle sx={{ m: 0, p: 2, position: 'relative' }}>
          <Typography fontSize={18} fontWeight={600}>
            Image View
          </Typography>
          <IconButton
            aria-label="close"
            onClick={handleClose}
            sx={{
              position: 'absolute',
              right: 8,
              top: 8,
              color: (theme) => theme.palette.grey[500],
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent dividers>
          <Box
            component="img"
            src={selectedImage}
            alt="Full Preview"
            sx={{
              width: 600,
              height: 350,
              objectFit: 'contain',
              display: 'block',
              mx: 'auto',
            }}
          />
        </DialogContent>
      </Dialog>

    </Box>
  );
};

export default TableComponent;